<template>
  <transition name="fade">
    <div
      class="screenshot-viewer"
      v-if="session.screenshotViewer"
      @click="closeViewer"
    >
      <div class="viewer-image-container">
        <img
          :src="session.screenshotViewer.imageData"
          alt="Grimoire Screenshot"
          class="viewer-image"
        />
      </div>
      <div class="viewer-countdown" v-if="session.screenshotViewer.mode === 'timer'">
        <div class="countdown-bar">
          <div class="countdown-fill" :style="{ width: countdownPercent + '%' }"></div>
        </div>
        <div class="countdown-text">{{ remainingSeconds }}s</div>
      </div>
      <div class="viewer-close-hint">
        {{ $t("chat.screenshotTapToClose") }}
      </div>
    </div>
  </transition>
</template>

<script>
import { mapState } from "vuex";

export default {
  data() {
    return {
      remainingSeconds: 0,
      countdownPercent: 100,
      timer: null,
    };
  },
  computed: {
    ...mapState(["session"]),
  },
  watch: {
    "session.screenshotViewer": function (viewer) {
      if (viewer) {
        this.startTimer(viewer);
      } else {
        this.stopTimer();
      }
    },
  },
  methods: {
    startTimer(viewer) {
      this.stopTimer();
      if (viewer.mode !== "timer") return;

      const ttl = viewer.ttl;
      const viewedAt = viewer.viewedAt;

      const tick = () => {
        const elapsed = (Date.now() - viewedAt) / 1000;
        this.remainingSeconds = Math.max(0, Math.ceil(ttl - elapsed));
        this.countdownPercent = Math.max(0, ((ttl - elapsed) / ttl) * 100);

        if (this.remainingSeconds <= 0) {
          this.stopTimer();
          this.expireAndClose();
        }
      };

      tick();
      this.timer = setInterval(tick, 200);
    },
    stopTimer() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },
    closeViewer() {
      this.$store.commit("session/closeScreenshotViewer");
    },
    expireAndClose() {
      const viewer = this.session.screenshotViewer;
      if (viewer) {
        // Find the chat that contains this screenshot to expire it
        for (let chatPlayerId in this.session.chats) {
          const msg = this.session.chats[chatPlayerId].messages.find(
            (m) => m.type === "screenshot" && m.msgId === viewer.msgId
          );
          if (msg) {
            this.$store.commit("session/screenshotExpired", {
              chatPlayerId,
              msgId: viewer.msgId,
            });
            break;
          }
        }
      }
      this.$store.commit("session/closeScreenshotViewer");
    },
  },
  mounted() {
    if (this.session.screenshotViewer) {
      this.startTimer(this.session.screenshotViewer);
    }
  },
  beforeUnmount() {
    this.stopTimer();
  },
};
</script>

<style lang="scss" scoped>
.screenshot-viewer {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.95);
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  touch-action: pinch-zoom;
  cursor: pointer;

  .viewer-image-container {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    overflow: auto;
    touch-action: pinch-zoom pan-x pan-y;
    cursor: default;

    .viewer-image {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
      touch-action: pinch-zoom pan-x pan-y;
    }
  }

  .viewer-countdown {
    position: absolute;
    bottom: 60px;
    left: 20px;
    right: 20px;
    display: flex;
    align-items: center;
    gap: 12px;

    .countdown-bar {
      flex: 1;
      height: 6px;
      background: rgba(255, 255, 255, 0.2);
      border-radius: 3px;
      overflow: hidden;

      .countdown-fill {
        height: 100%;
        background: linear-gradient(90deg, #f59e0b, #ef4444);
        border-radius: 3px;
        transition: width 0.2s linear;
      }
    }

    .countdown-text {
      font-size: 16px;
      font-weight: bold;
      color: white;
      min-width: 40px;
      text-align: right;
    }
  }

  .viewer-close-hint {
    position: absolute;
    bottom: 20px;
    text-align: center;
    font-size: 12px;
    opacity: 0.5;
    color: white;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
